import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { StorageRounded } from '@mui/icons-material'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useUser } from '@src/shared/contexts/UserContext'
import { useToast } from '@src/shared/hooks/useToast'
import {
  useAnonymizationDashboard,
  useContainerStatuses,
  useDatabasePendingApprovalCounts,
  useResetContainerAnonymization,
  useStartContainerAnonymization,
  useTerminateContainerOrchestration,
  useToggleContainerDisabled,
  useUnblockContainer,
} from '@src/tools/anonymizer/api/container-status-api'
import type { ContainerAnonymizationDashboard } from '@src/tools/anonymizer/types/Constants'
import { ErrorTooltip } from './ErrorTooltip'

const formatCount = (n: number): string => n.toLocaleString('fr-CA')

const formatTimestamp = (ts: number): string => {
  if (!ts) return '—'
  const d = new Date(ts * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const formatTimeDiff = (prodTs: number, lastProcessedTs: number): string => {
  if (!prodTs || !lastProcessedTs) return '—'
  const diffSec = Math.abs(prodTs - lastProcessedTs)
  const days = Math.floor(diffSec / 86400)
  const hours = Math.floor((diffSec % 86400) / 3600)
  const minutes = Math.floor((diffSec % 3600) / 60)
  const seconds = diffSec % 60
  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`)
  return parts.join(' ')
}

const getTimeDiffSx = (prodTs: number, lastProcessedTs: number) => {
  const isLagging = prodTs && lastProcessedTs && Math.abs(prodTs - lastProcessedTs) > 900
  return isLagging ? { color: 'warning.main', fontWeight: 600 } : undefined
}

const getDeltaSx = (delta: number) => ({
  color: delta > 100 ? 'warning.main' : 'success.main',
  fontWeight: 600,
})

const rowKey = (s: ContainerAnonymizationDashboard) => `${s.dbName}.${s.containerName}`

const AnonymizationStatusPage = () => {
  const { t } = useTranslation()

  const getStatusChip = (s: ContainerAnonymizationDashboard) => {
    if (s.isDisabled)
      return (
        <Tooltip title={s.disabledReason || ''} arrow>
          <Chip size="small" label={t('anonymizer.blockStatus.disabled', 'Disabled')} color="info" sx={{ fontWeight: 600 }} />
        </Tooltip>
      )
    if (s.isBlocked)
      return (
        <Tooltip title={s.blockedReason || ''} arrow>
          <Chip size="small" label={t('anonymizer.blockStatus.blocked', 'Blocked')} color="error" sx={{ fontWeight: 600 }} />
        </Tooltip>
      )
    return <Chip size="small" label={t('anonymizer.blockStatus.ok', 'OK')} color="success" sx={{ fontWeight: 600 }} />
  }
  const { user } = useUser()
  const canResetAnonymization = user?.rights.resetAnonymization ?? false
  const { showSuccess, ToastComponent } = useToast()
  const [dbSummaries] = useDatabasePendingApprovalCounts()
  const [containerStatuses] = useContainerStatuses()
  const toggleDisabled = useToggleContainerDisabled()
  const unblock = useUnblockContainer()
  const terminateOrchestration = useTerminateContainerOrchestration()
  const startAnonymization = useStartContainerAnonymization()
  const resetAnonymization = useResetContainerAnonymization()

  const [searchParams, setSearchParams] = useSearchParams()
  const selectedDb = searchParams.get('db')

  const [statuses, loading] = useAnonymizationDashboard(selectedDb)

  const setSelectedDb = (db: string | null) => {
    setSearchParams((prev) => {
      if (db) prev.set('db', db)
      else prev.delete('db')
      return prev
    })
    setSelected(new Set())
  }

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [disableDialogOpen, setDisableDialogOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [actionInProgress, setActionInProgress] = useState(false)
  const [resetDialogOpen, setResetDialogOpen] = useState(false)

  const dbNames = useMemo(() => dbSummaries.map((s) => s.dbName).sort(), [dbSummaries])

  const blockedCountByDb = useMemo(() => {
    const acc: Record<string, number> = {}
    for (const cs of containerStatuses) {
      if (cs.isBlocked && !cs.isDisabled) {
        acc[cs.dbName] = (acc[cs.dbName] || 0) + 1
      }
    }
    return acc
  }, [containerStatuses])

  const visibleContainers = useMemo(
    () => [...statuses].sort((a, b) => a.containerName.localeCompare(b.containerName)),
    [statuses],
  )

  const visibleKeys = useMemo(() => visibleContainers.map(rowKey), [visibleContainers])
  const allSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k))
  const someSelected = visibleKeys.some((k) => selected.has(k)) && !allSelected

  const toggleAll = () => {
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev)
        for (const k of visibleKeys) next.delete(k)
        return next
      })
    } else {
      setSelected((prev) => new Set([...prev, ...visibleKeys]))
    }
  }

  const toggleRow = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectedStatuses = useMemo(
    () => statuses.filter((s) => selected.has(rowKey(s))),
    [statuses, selected],
  )

  const hasSelection = selectedStatuses.length > 0
  const canUnblock = hasSelection && selectedStatuses.every((s) => s.isBlocked)
  const canEnable = hasSelection && selectedStatuses.every((s) => s.isDisabled)
  const canDisable = hasSelection && selectedStatuses.every((s) => !s.isDisabled)
  const canTerminate = hasSelection && selectedStatuses.some((s) => s.isOrchestrationRunning)
  const canStart = hasSelection && selectedStatuses.every((s) => !s.isDisabled && !s.isBlocked && !s.isOrchestrationRunning)
  const canReset = canResetAnonymization && hasSelection && selectedStatuses.every((s) => !s.isDisabled && !s.isBlocked)

  const runBulkAction = async (
    action: (s: ContainerAnonymizationDashboard) => Promise<unknown>,
    successKey: string,
  ) => {
    setActionInProgress(true)
    try {
      await Promise.all(selectedStatuses.map(action))
      showSuccess(t(successKey as never))
      setSelected(new Set())
    } finally {
      setActionInProgress(false)
    }
  }

  const handleBulkTerminate = () =>
    runBulkAction(
      (s) => terminateOrchestration.mutateAsync({ dbName: s.dbName, containerName: s.containerName }),
      'anonymizer.status.successTerminated',
    )

  const handleBulkUnblock = () =>
    runBulkAction(
      (s) => unblock.mutateAsync({ dbName: s.dbName, containerName: s.containerName }),
      'anonymizer.blockStatus.successUnblocked',
    )

  const handleBulkEnable = () =>
    runBulkAction(
      (s) =>
        toggleDisabled.mutateAsync({
          dbName: s.dbName,
          containerName: s.containerName,
          body: { isDisabled: false },
        }),
      'anonymizer.blockStatus.successEnabled',
    )

  const handleBulkDisable = async () => {
    setActionInProgress(true)
    try {
      await Promise.all(
        selectedStatuses.map((s) =>
          toggleDisabled.mutateAsync({
            dbName: s.dbName,
            containerName: s.containerName,
            body: { isDisabled: true, reason: reason || undefined },
          }),
        ),
      )
      showSuccess(t('anonymizer.blockStatus.successDisabled'))
      setSelected(new Set())
    } finally {
      setActionInProgress(false)
      setDisableDialogOpen(false)
      setReason('')
    }
  }

  const handleBulkStart = () =>
    runBulkAction(
      (s) => startAnonymization.mutateAsync({ dbName: s.dbName, containerName: s.containerName }),
      'anonymizer.status.successStarted',
    )

  const handleBulkReset = async () => {
    setActionInProgress(true)
    try {
      await Promise.all(
        selectedStatuses.map((s) =>
          resetAnonymization.mutateAsync({ dbName: s.dbName, containerName: s.containerName }),
        ),
      )
      showSuccess(t('anonymizer.status.successReset'))
      setSelected(new Set())
    } finally {
      setActionInProgress(false)
      setResetDialogOpen(false)
    }
  }

  const getDbChip = (dbName: string) => {
    const blockedCount = blockedCountByDb[dbName] || 0
    if (blockedCount > 0)
      return <Chip size="small" label={blockedCount} color="error" sx={{ borderRadius: '12px', fontWeight: 600, minWidth: 32 }} />
    return <Chip size="small" label={t('anonymizer.blockStatus.ok')} color="success" sx={{ borderRadius: '12px', fontWeight: 600, minWidth: 32 }} />
  }

  return (
    <>
      <Paper sx={{ mt: 2, mb: 2, borderRadius: 2, display: 'flex', height: 'calc(100vh - 180px)' }}>
        {/* Database sidebar */}
        <Box sx={{ width: 260, minWidth: 260, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
          <List dense>
            <ListSubheader>{t('anonymizer.databases')}</ListSubheader>
            {dbNames.map((dbName) => (
              <ListItemButton
                key={dbName}
                selected={selectedDb === dbName}
                onClick={() => setSelectedDb(dbName)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <StorageRounded fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={dbName} />
                {getDbChip(dbName)}
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Main content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Toolbar variant="dense" sx={{ gap: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {selected.size > 0
                ? `${selected.size} ${t('anonymizer.status.selected')}`
                : selectedDb ?? t('anonymizer.status.title')}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={!canStart || actionInProgress}
                onClick={handleBulkStart}
              >
                {t('anonymizer.status.start')}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                disabled={!canTerminate || actionInProgress}
                onClick={handleBulkTerminate}
              >
                {t('anonymizer.status.terminate')}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                disabled={!canUnblock || actionInProgress}
                onClick={handleBulkUnblock}
              >
                {t('anonymizer.blockStatus.unblock')}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={!canEnable || actionInProgress}
                onClick={handleBulkEnable}
              >
                {t('anonymizer.blockStatus.enable')}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                disabled={!canDisable || actionInProgress}
                onClick={() => setDisableDialogOpen(true)}
              >
                {t('anonymizer.blockStatus.disable')}
              </Button>
              {canResetAnonymization && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={!canReset || actionInProgress}
                  onClick={() => setResetDialogOpen(true)}
                >
                  {t('anonymizer.status.reset')}
                </Button>
              )}
            </Stack>
          </Toolbar>

          {selectedDb && loading && statuses.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
              <CircularProgress />
            </Box>
          ) : selectedDb ? (
            <TableContainer sx={{ flexGrow: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someSelected}
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                    </TableCell>
                    <TableCell>{t('anonymizer.status.container')}</TableCell>
                    <TableCell>{t('anonymizer.columns.status')}</TableCell>
                    <TableCell>{t('anonymizer.status.orchestration')}</TableCell>
                    <TableCell align="right">{t('anonymizer.status.prodCount')}</TableCell>
                    <TableCell align="right">{t('anonymizer.status.anonymizedCount')}</TableCell>
                    <TableCell align="right">{t('anonymizer.status.delta')}</TableCell>
                    <TableCell>{t('anonymizer.status.prodTimestamp')}</TableCell>
                    <TableCell>{t('anonymizer.status.lastProcessed')}</TableCell>
                    <TableCell>{t('anonymizer.status.timeDiff')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleContainers.map((s) => {
                    const key = rowKey(s)
                    if (s.error) {
                      return (
                        <TableRow key={key}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={selected.has(key)} onChange={() => toggleRow(key)} />
                          </TableCell>
                          <TableCell>{s.containerName}</TableCell>
                          <TableCell>{getStatusChip(s)}</TableCell>
                          <TableCell colSpan={6}>
                            <ErrorTooltip error={s.error} />
                          </TableCell>
                        </TableRow>
                      )
                    }
                    const delta = s.prodCount - s.anonymizedCount
                    return (
                      <TableRow key={key} selected={selected.has(key)}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selected.has(key)} onChange={() => toggleRow(key)} />
                        </TableCell>
                        <TableCell>{s.containerName}</TableCell>
                        <TableCell>{getStatusChip(s)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.isOrchestrationRunning ? t('anonymizer.status.running') : t('anonymizer.status.notRunning')}
                            color={s.isOrchestrationRunning ? 'primary' : 'default'}
                            variant={s.isOrchestrationRunning ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">{formatCount(s.prodCount)}</TableCell>
                        <TableCell align="right">{formatCount(s.anonymizedCount)}</TableCell>
                        <TableCell align="right" sx={getDeltaSx(delta)}>
                          {formatCount(delta)}
                        </TableCell>
                        <TableCell>{formatTimestamp(s.prodTimestamp)}</TableCell>
                        <TableCell>{formatTimestamp(s.lastProcessedTimestamp)}</TableCell>
                        <TableCell sx={getTimeDiffSx(s.prodTimestamp, s.lastProcessedTimestamp)}>
                          {formatTimeDiff(s.prodTimestamp, s.lastProcessedTimestamp)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
              <Typography color="text.secondary">{t('anonymizer.status.selectDb')}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('anonymizer.blockStatus.disableDialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('anonymizer.blockStatus.disableDialogText')}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label={t('anonymizer.blockStatus.reasonLabel')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDisableDialogOpen(false); setReason('') }}>
            {t('anonymizer.blockStatus.cancel')}
          </Button>
          <Button onClick={handleBulkDisable} variant="contained" color="warning" disabled={actionInProgress}>
            {t('anonymizer.blockStatus.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('anonymizer.status.resetConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('anonymizer.status.resetConfirmText')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>
            {t('anonymizer.blockStatus.cancel')}
          </Button>
          <Button onClick={handleBulkReset} variant="contained" color="error" disabled={actionInProgress}>
            {t('anonymizer.blockStatus.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastComponent />
    </>
  )
}

export default AnonymizationStatusPage
